import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  BarChart3,
  Calendar as CalendarIcon,
  DollarSign,
  TrendingUp,
  Receipt,
  RefreshCw,
  Loader2,
  FileText,
  Lock,
  ShieldCheck,
  ShieldAlert,
  Database,
  History,
  Download,
  Search,
  Activity,
  ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

const safeDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '-';
  }
};

const Row = ({ label, value, strong = false }) => (
  <div className="flex justify-between gap-3 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={strong ? 'font-bold text-right' : 'text-right'}>{value}</span>
  </div>
);

const ReportsPage = () => {
  const { api, hasFeature } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState('dashboard');

  const [dailySummary, setDailySummary] = useState(null);
  const [xReading, setXReading] = useState(null);
  const [zReadings, setZReadings] = useState([]);
  const [fiscalStatus, setFiscalStatus] = useState(null);
  const [ejournalVerify, setEjournalVerify] = useState(null);
  const [ejournalEntries, setEjournalEntries] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [birStatus, setBirStatus] = useState(null);
  const [birSelfTest, setBirSelfTest] = useState(null);
  const [birReceiptSequence, setBirReceiptSequence] = useState(null);
  const [birHashChain, setBirHashChain] = useState(null);

  const [creatingZReading, setCreatingZReading] = useState(false);
  const [checkingJournal, setCheckingJournal] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');

      const requests = await Promise.allSettled([
        api.get(`/reports/daily?report_date=${dateStr}`),
        api.get('/reports/x-reading'),
        api.get('/reports/z-readings'),
        api.get('/reports/fiscal/status'),
        api.get('/reports/ejournal/verify'),
        api.get('/reports/ejournal?limit=50'),
        api.get('/reports/audit?limit=50'),
        api.get('/bir/status'),
        api.get('/bir/receipt-sequence'),
        api.get('/bir/hash-chain'),
      ]);

      if (requests[0].status === 'fulfilled') setDailySummary(requests[0].value.data);
      if (requests[1].status === 'fulfilled') setXReading(requests[1].value.data);
      if (requests[2].status === 'fulfilled') setZReadings(requests[2].value.data || []);
      if (requests[3].status === 'fulfilled') setFiscalStatus(requests[3].value.data);
      if (requests[4].status === 'fulfilled') setEjournalVerify(requests[4].value.data);
      if (requests[5].status === 'fulfilled') setEjournalEntries(requests[5].value.data?.entries || requests[5].value.data || []);
      if (requests[6].status === 'fulfilled') setAuditLogs(requests[6].value.data?.logs || requests[6].value.data || []);
      if (requests[7].status === 'fulfilled') setBirStatus(requests[7].value.data);
      if (requests[8].status === 'fulfilled') setBirReceiptSequence(requests[8].value.data);
      if (requests[9].status === 'fulfilled') setBirHashChain(requests[9].value.data);

      const failed = requests.filter(r => r.status === 'rejected').length;
      if (failed) toast.warning(`${failed} report section(s) could not be loaded`);
    } catch (error) {
      toast.error('Failed to load BIR compliance reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [api, selectedDate]);

  const handleCreateZReading = async () => {
    setCreatingZReading(true);
    try {
      await api.post('/reports/z-reading');
      toast.success('Z Reading created and business day locked');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Z Reading');
    } finally {
      setCreatingZReading(false);
    }
  };

  const handleVerifyJournal = async () => {
    setCheckingJournal(true);
    try {
      const res = await api.get('/reports/ejournal/verify');
      setEjournalVerify(res.data);
      toast.success('E-Journal hash chain verified');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-Journal verification failed');
    } finally {
      setCheckingJournal(false);
    }
  };

  const handleExportJournal = () => {
    window.open('/api/reports/ejournal/export.csv', '_blank');
  };

  const handleRunBirSelfTest = async () => {
    try {
      const res = await api.post('/bir/self-test', {});
      setBirSelfTest(res.data);
      if (res.data?.passed) {
        toast.success('BIR fiscal self-test passed');
      } else {
        toast.error('BIR fiscal self-test failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'BIR fiscal self-test failed');
    }
  };

  const handleOpenCertificationReport = () => {
    const token = localStorage.getItem('pos_token');
    if (!token) {
      toast.error('Login token not found');
      return;
    }

    const url = `/api/bir/certification.html?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };

  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: ShieldCheck },
    { key: 'sales', label: 'Sales / VAT', icon: BarChart3 },
    { key: 'readings', label: 'X / Z Readings', icon: Lock },
    { key: 'ejournal', label: 'E-Journal', icon: Database },
    { key: 'audit', label: 'Audit Trail', icon: History },
    { key: 'bir', label: 'BIR Self-Test', icon: ShieldAlert },
  ];

  const journalHealthy = Boolean(
    birStatus?.healthy ||
    birHashChain?.valid ||
    ejournalVerify?.valid ||
    ejournalVerify?.is_valid ||
    ejournalVerify?.status === 'valid' ||
    ejournalVerify?.status === 'healthy'
  );

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">BIR Compliance Center</h1>
          <p className="text-muted-foreground">
            Fiscal status, X/Z readings, VAT summary, e-journal, and audit evidence
          </p>
        </div>

        <div className="flex gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2">
                <CalendarIcon className="w-4 h-4" />
                {format(selectedDate, 'MMM dd, yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Button variant="outline" size="icon" onClick={loadData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.key)}
              className="gap-2"
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </Button>
          );
        })}
      </div>

      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <ShieldCheck className="w-7 h-7 text-green-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Fiscal Health</p>
                    <p className="text-xl font-bold">
                      {journalHealthy ? 'Healthy' : 'Needs Review'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Receipt className="w-7 h-7 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Transactions</p>
                    <p className="text-xl font-bold">{dailySummary?.total_transactions || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <DollarSign className="w-7 h-7 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Gross Sales</p>
                    <p className="text-xl font-bold">{formatCurrency(dailySummary?.total_sales)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Lock className="w-7 h-7 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Latest Z Reading</p>
                    <p className="text-xl font-bold">
                      {zReadings?.[0]?.reading_number ? `#${zReadings[0].reading_number}` : '-'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Fiscal Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Row label="Fiscal Health" value={birStatus?.healthy ? 'PASS' : 'Needs Review'} strong />
                <Row label="Last Receipt" value={birStatus?.last_receipt || fiscalStatus?.last_receipt_number || '-'} />
                <Row label="Highest Receipt" value={birStatus?.highest_receipt || birReceiptSequence?.highest || '-'} />
                <Row label="Receipt Counter" value={birStatus?.receipt_sequence_counter ?? fiscalStatus?.receipt_sequence ?? '-'} />
                <Row label="Reset Counter" value={birStatus?.reset_counter ?? fiscalStatus?.reset_counter ?? '-'} />
                <Row label="Grand Total Accumulator" value={formatCurrency(birStatus?.grand_total_accumulator ?? fiscalStatus?.grand_total_accumulator)} strong />
                <Row label="Duplicate Receipts" value={birStatus?.duplicates ?? birReceiptSequence?.duplicate_count ?? '-'} />
                <Row label="Missing Receipts" value={birStatus?.missing_receipts ?? birReceiptSequence?.missing_count ?? '-'} />
                <Row label="Last Z Reading" value={safeDate(birStatus?.last_z || fiscalStatus?.last_z_reading_at || zReadings?.[0]?.locked_at)} />
                <Row label="Business Day Lock" value={birStatus?.locked_today ? 'Locked / Z Reading Exists' : 'Open'} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {journalHealthy ? <ShieldCheck className="w-5 h-5 text-green-600" /> : <ShieldAlert className="w-5 h-5 text-orange-600" />}
                  E-Journal Integrity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Row label="Hash Chain Status" value={journalHealthy ? 'Valid' : (ejournalVerify?.status || 'Needs Review')} strong />
                <Row label="Entries Verified" value={birHashChain?.entries ?? ejournalVerify?.entries_verified ?? ejournalVerify?.total_entries ?? ejournalEntries.length ?? 0} />
                <Row label="Broken Links" value={birHashChain?.broken_links ?? 0} />
                <Row label="Last Sequence" value={birHashChain?.last_sequence ?? ejournalVerify?.last_sequence ?? ejournalEntries?.[0]?.sequence ?? '-'} />
                <Row label="Last Checked" value={safeDate(birHashChain?.checked_at || ejournalVerify?.checked_at || ejournalVerify?.generated_at)} />
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={handleVerifyJournal} disabled={checkingJournal}>
                    {checkingJournal ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                    Verify Hash Chain
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleExportJournal}>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'sales' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Sales Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Row label="Total Sales" value={formatCurrency(dailySummary?.total_sales)} strong />
              <Row label="Transactions" value={dailySummary?.total_transactions || 0} />
              <Row label="Cash Sales" value={formatCurrency(dailySummary?.cash_sales)} />
              <Row label="QR Sales" value={formatCurrency(dailySummary?.qr_sales)} />
              <Row label="Online Sales" value={formatCurrency(dailySummary?.online_sales)} />
              <Row label="Voids" value={`${dailySummary?.void_count || 0} / ${formatCurrency(dailySummary?.void_amount)}`} />
              <Row label="Refunds" value={`${dailySummary?.refund_count || 0} / ${formatCurrency(dailySummary?.refund_amount)}`} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>VAT / Discount Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Row label="VATable Sales" value={formatCurrency(dailySummary?.vatable_sales)} />
              <Row label="VAT Exempt Sales" value={formatCurrency(dailySummary?.vat_exempt_sales)} />
              <Row label="Zero-Rated Sales" value={formatCurrency(dailySummary?.zero_rated_sales)} />
              <Row label="VAT Collected" value={formatCurrency(dailySummary?.total_vat)} strong />
              <Row label="Regular Discounts" value={formatCurrency(dailySummary?.regular_discounts)} />
              <Row label="SC/PWD Discounts" value={formatCurrency(dailySummary?.special_discounts)} />
              <Row label="Less VAT" value={formatCurrency(dailySummary?.less_vat)} />
              <Row label="Total Discounts" value={formatCurrency(dailySummary?.total_discounts)} strong />
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'readings' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                X Reading / Live Snapshot
              </CardTitle>
              <Badge variant="secondary">Live</Badge>
            </CardHeader>
            <CardContent className="space-y-3">
              <Row label="Generated" value={safeDate(xReading?.generated_at)} />
              <Row label="Total Sales" value={formatCurrency(xReading?.summary?.total_sales)} strong />
              <Row label="Transactions" value={xReading?.summary?.total_transactions || 0} />
              <Row label="VAT" value={formatCurrency(xReading?.summary?.total_vat)} />
              <Row label="Cash" value={formatCurrency(xReading?.payment_breakdown?.cash)} />
              <Row label="QR Manual" value={formatCurrency(xReading?.payment_breakdown?.qr_manual)} />
              <Row label="GCash" value={formatCurrency(xReading?.payment_breakdown?.gcash)} />
              <Row label="Maya" value={formatCurrency(xReading?.payment_breakdown?.maya)} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Z Readings / Locked Days
              </CardTitle>
              {hasFeature('FEATURE_Z_READING_LOCK') && (
                <Button size="sm" onClick={handleCreateZReading} disabled={creatingZReading}>
                  {creatingZReading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4 mr-1" />}
                  Lock Today
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[360px]">
                {zReadings.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No Z readings yet</p>
                ) : (
                  <div className="space-y-3">
                    {zReadings.map(reading => (
                      <div key={reading.id} className="p-4 rounded-lg border border-border">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="font-medium">Z Reading #{reading.reading_number}</div>
                            <div className="text-sm text-muted-foreground">{safeDate(reading.reading_date)}</div>
                          </div>
                          <Badge variant="outline">
                            <Lock className="w-3 h-3 mr-1" />
                            Locked
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <Row label="GTA" value={formatCurrency(reading.grand_total_accumulator)} strong />
                          <Row label="Sales" value={formatCurrency(reading.total_sales)} />
                          <Row label="Net Sales" value={formatCurrency(reading.net_sales)} />
                          <Row label="VAT" value={formatCurrency(reading.total_vat)} />
                          <Row label="Transactions" value={reading.transaction_count} />
                          <Row label="Voids / Refunds" value={`${reading.void_count || 0} / ${reading.refund_count || 0}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'ejournal' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Electronic Journal
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleVerifyJournal}>
                <ShieldCheck className="w-4 h-4 mr-2" />
                Verify
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportJournal}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[520px]">
              {ejournalEntries.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No e-journal entries loaded</p>
              ) : (
                <div className="space-y-3">
                  {ejournalEntries.map(entry => (
                    <div key={entry.id || entry.sequence} className="p-4 rounded-lg border border-border">
                      <div className="flex justify-between gap-3">
                        <div>
                          <div className="font-medium">
                            #{entry.sequence} — {entry.event_type}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {entry.target_type} / {entry.target_id}
                          </div>
                        </div>
                        <Badge variant="outline">{safeDate(entry.created_at)}</Badge>
                      </div>
                      <div className="mt-3 text-xs font-mono break-all text-muted-foreground">
                        Hash: {entry.entry_hash || '-'}
                      </div>
                      <div className="text-xs font-mono break-all text-muted-foreground">
                        Prev: {entry.previous_hash || '-'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {activeTab === 'bir' && (
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                BIR Fiscal Self-Test
              </CardTitle>
              <div className="flex gap-2">
                <Button onClick={handleRunBirSelfTest} className="gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Run Self-Test
                </Button>
                <Button variant="outline" onClick={handleOpenCertificationReport} className="gap-2">
                  <Download className="w-4 h-4" />
                  Certification Report
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Row label="Overall Status" value={birSelfTest?.passed ?? birStatus?.healthy ? 'PASS' : 'Needs Review'} strong />
              <Row label="Receipt Sequence" value={birStatus?.receipt_sequence || '-'} />
              <Row label="Hash Chain" value={birStatus?.hash_chain || '-'} />
              <Row label="Duplicate Receipts" value={birStatus?.duplicates ?? birReceiptSequence?.duplicate_count ?? '-'} />
              <Row label="Missing Receipts" value={birStatus?.missing_receipts ?? birReceiptSequence?.missing_count ?? '-'} />
              <Row label="E-Journal Entries" value={birStatus?.ejournal_entries ?? birHashChain?.entries ?? '-'} />
              <Row label="Reprints" value={birStatus?.reprints ?? '-'} />
              <Row label="Voids" value={birStatus?.voids ?? '-'} />
              <Row label="Refunds" value={birStatus?.refunds ?? '-'} />
              <Row label="Checked At" value={safeDate(birSelfTest?.checked_at || birStatus?.checked_at)} />
            </CardContent>
          </Card>

          {birSelfTest?.checks && (
            <Card>
              <CardHeader>
                <CardTitle>Self-Test Results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {birSelfTest.checks.map((check, index) => (
                  <div key={`${check.name}-${index}`} className="p-4 rounded-lg border border-border">
                    <div className="flex justify-between items-center">
                      <div className="font-medium">{check.name}</div>
                      <Badge variant={check.status === 'PASS' ? 'default' : check.status === 'WARN' ? 'secondary' : 'destructive'}>
                        {check.status}
                      </Badge>
                    </div>
                    <pre className="mt-3 text-xs bg-muted rounded p-3 overflow-auto">
                      {JSON.stringify(check.details || {}, null, 2)}
                    </pre>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Receipt Sequence Evidence</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Row label="Valid" value={birReceiptSequence?.valid ? 'YES' : 'NO'} strong />
                <Row label="Total Receipts" value={birReceiptSequence?.total_receipts ?? '-'} />
                <Row label="Highest Receipt" value={birReceiptSequence?.highest || '-'} />
                <Row label="Highest Sequence" value={birReceiptSequence?.highest_sequence ?? '-'} />
                <Row label="Duplicate Count" value={birReceiptSequence?.duplicate_count ?? '-'} />
                <Row label="Missing Count" value={birReceiptSequence?.missing_count ?? '-'} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Hash Chain Evidence</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Row label="Valid" value={birHashChain?.valid ? 'YES' : 'NO'} strong />
                <Row label="Entries" value={birHashChain?.entries ?? '-'} />
                <Row label="Broken Links" value={birHashChain?.broken_links ?? '-'} />
                <Row label="Last Sequence" value={birHashChain?.last_sequence ?? '-'} />
                <div className="text-xs font-mono break-all text-muted-foreground">
                  Last Hash: {birHashChain?.last_hash || '-'}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}


      {activeTab === 'audit' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Audit Trail
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[520px]">
              {auditLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No audit logs loaded</p>
              ) : (
                <div className="space-y-3">
                  {auditLogs.map(log => (
                    <div key={log.id || `${log.action}-${log.created_at}`} className="p-4 rounded-lg border border-border">
                      <div className="flex justify-between gap-3">
                        <div>
                          <div className="font-medium">{log.action}</div>
                          <div className="text-sm text-muted-foreground">
                            {log.target_type || '-'} / {log.target_id || '-'}
                          </div>
                        </div>
                        <Badge variant="outline">{safeDate(log.created_at)}</Badge>
                      </div>
                      <pre className="mt-3 text-xs bg-muted rounded p-3 overflow-auto">
                        {JSON.stringify(log.details || {}, null, 2)}
                      </pre>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsPage;
